import { bpsToPercentString } from '@/utils/bps';
/**
 * The statutory payroll rates, in **one place**.
 *
 * Rates change; the code should not need surgery when they do. Nothing outside
 * this module may write `0.2` or `* 2 / 100` at a call site — every percentage
 * the payroll calculation applies comes from here.
 *
 * Rates are held in **basis points** (1 bp = 1/10000) so the arithmetic stays in
 * integers the whole way down: `tetri * bps / 10000`, rounded once. A float rate
 * would reintroduce exactly the drift the tetri contract exists to prevent.
 *
 * The values are Georgia's, verified against the brief:
 *
 * - **Personal income tax 20%**, flat, on gross, withheld by the employer as tax
 *   agent.
 * - **Pension 2% employee + 2% employer**, both on gross. The employee's share is
 *   withheld (a liability carved out of gross, not an expense); the employer's is
 *   a genuine additional company cost.
 * - **State contribution up to 2%**, paid by the government straight into the
 *   employee's pension account. It **never touches the company's books** and is
 *   held here only so the screen can show it, clearly labelled as outside the
 *   journal entry.
 *
 * OPEN ITEM: the state contribution is banded by income in the real scheme (it
 * tapers above certain annual thresholds). The flat 2% here is the headline rate
 * and is display-only, so an inaccurate band cannot corrupt a posting — but it
 * must be confirmed with the accountants before it is shown as advice.
 */

export type PayrollRates = {
    /** Personal income tax, basis points of gross. Withheld. */
    incomeTaxBps: number;
    /** Employee pension contribution, basis points of gross. Withheld. */
    employeePensionBps: number;
    /** Employer pension contribution, basis points of gross. A real expense. */
    employerPensionBps: number;
    /**
     * State pension contribution, basis points of gross. **Never journalized** —
     * information only.
     */
    stateContributionBps: number;
};

/** The statutory defaults: 20% / 2% / 2% (+ 2% state, informational). */
export const PAYROLL_RATES: PayrollRates = {
    incomeTaxBps: 2000,
    employeePensionBps: 200,
    employerPensionBps: 200,
    stateContributionBps: 200,
};

/**
 * Basis points as a display percentage (`2000` → `"20"`). Re-exported from
 * `@/utils/bps` (BOS-162) so every screen rounds a rate the same way.
 */
export const bpsToPercentText = bpsToPercentString;
